import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Badge,
  Modal,
  TextInput,
  Select,
  ToggleSwitch,
  Spinner,
} from "flowbite-react";
import {
  HiPlus,
  HiAdjustments,
  HiSearch,
  HiOutlineTrash,
  HiOutlinePencil,
  HiOutlineExclamation,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
} from "react-icons/hi";
import axios from "../config/axiosInstance";
import { useNavigate, useLocation } from "react-router-dom";
import { CRM, CUSTOM_FIELDS } from "../config/apiConfig";
import CorporateBreadcrumb from "../components/common/CorporateBreadcrumb";
// Se quiser, crie isso no apiConfig e importe de lá
// import { CUSTOM_FIELDS } from "../config/apiConfig";

const ENTITY_LABELS = {
  lead: "Lead",
  card: "Card (Funil)",
  company: "Empresa",
  activity: "Atividade",
};

const TYPE_LABELS = {
  text: "Texto",
  textarea: "Texto longo",
  number: "Número",
  currency: "Moeda",
  boolean: "Verdadeiro/Falso",
  date: "Data",
  single_select: "Seleção única",
  multi_select: "Seleção múltipla",
};

const SCOPE_LABELS = {
  global: "Global",
  funnel: "Por funil",
};

function cx(...c) {
  return c.filter(Boolean).join(" ");
}

function slugify(str = "") {
  return String(str)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_") // tudo que não é letra/número vira _
    .replace(/^_+|_+$/g, "") // remove _ do início/fim
    .replace(/_{2,}/g, "_") // evita __ duplo
    .slice(0, 60);
}

/* ===================== Form Modal ===================== */
function FieldFormModal({
  open,
  onClose,
  onSaved,
  initialField,
  funnelId: currentFunnelId,
}) {
  const isEdit = !!initialField?.id;

  const [label, setLabel] = useState("");
  const [key, setKey] = useState("");
  const [scope, setScope] = useState("global");
  const [type, setType] = useState("text");
  const [description, setDescription] = useState("");
  const [required, setRequired] = useState(false);
  const [active, setActive] = useState(true);
  const [showOnCard, setShowOnCard] = useState(true);
  const [showOnDetails, setShowOnDetails] = useState(true);
  const [group, setGroup] = useState("");
  const [order, setOrder] = useState("");
  const [optionsInput, setOptionsInput] = useState("");
  const [options, setOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initialField) {
      setLabel(initialField.label || "");
      setKey(initialField.key || "");
      setScope(initialField.scope || "global");
      setType(initialField.type || "text");
      setDescription(initialField.description || "");
      setRequired(!!initialField.required);
      setActive(initialField.active !== false);
      setShowOnCard(initialField.showOnCard !== false);
      setShowOnDetails(initialField.showOnDetails !== false);
      setGroup(initialField.group || "");
      setOrder(initialField.order ?? "");
      setOptions(
        Array.isArray(initialField.options)
          ? initialField.options
              .map((opt) => {
                if (typeof opt === "string") {
                  return {
                    id: undefined,
                    label: opt,
                    value: opt,
                  };
                }

                const label = opt.label ?? opt.value ?? "";
                if (!label) return null;

                return {
                  id: opt.id,
                  label,
                  value: opt.value ?? label,
                };
              })
              .filter(Boolean)
          : []
      );

      setOptionsInput("");
    } else {
      setLabel("");
      setKey("");
      setScope("global");
      setType("text");
      setDescription("");
      setRequired(false);
      setActive(true);
      setShowOnCard(true);
      setShowOnDetails(true);
      setGroup("");
      setOrder("");
      setOptions([]);
      setOptionsInput("");
    }
  }, [open, initialField]);

  // preenche key automaticamente quando digitando label (apenas em modo criação)
  useEffect(() => {
    if (!open) return;
    if (isEdit) return;
    if (!label) {
      setKey("");
      return;
    }

    setKey(slugify(label));
  }, [label, isEdit, open]);

  const handleAddOptionsFromInput = () => {
    const text = optionsInput.trim();
    if (!text) return;

    const parts = text
      .split(/[,\n]/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (!parts.length) return;

    setOptions((prev) => {
      const existingLabels = new Set(prev.map((o) => o.label.toLowerCase()));

      const newOptions = [...prev];

      parts.forEach((p) => {
        if (!existingLabels.has(p.toLowerCase())) {
          newOptions.push({
            id: undefined, // backend pode gerar depois
            label: p,
            value: p,
          });
        }
      });

      return newOptions;
    });

    setOptionsInput("");
  };

  const handleRemoveOption = (indexToRemove) => {
    setOptions((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const handleSubmit = async () => {
    if (!label.trim() || !key.trim() || !currentFunnelId) return;

    try {
      setSaving(true);

      const payload = {
        id: isEdit ? initialField.id : undefined,
        label: label.trim(),
        key: key.trim(),
        entity: "card",
        scope,
        funnelId: scope === "funnel" ? currentFunnelId : null,
        type,
        helpText: description.trim() || null,
        required,
        active,
        showOnCard,
        showOnDetails,
        group: group.trim() || null,
        order: order === "" ? null : Number(order),
        options:
          type === "single_select" || type === "multi_select"
            ? options?.map((opt) => ({
                id: opt.id, // mantém se já existe
                label: opt.label,
                value: opt.value ?? opt.label,
              }))
            : undefined,
      };

      await axios.post(CUSTOM_FIELDS.UPSERT(currentFunnelId), payload);

      await onSaved?.();
      onClose();
    } catch (e) {
      console.error("Erro ao salvar campo customizado", e);
      // TODO: toast
    } finally {
      setSaving(false);
    }
  };

  const isChoiceType = type === "single_select" || type === "multi_select";

  return (
    <Modal show={open} onClose={onClose} size="5xl">
      <Modal.Header>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-gray-900">
            {isEdit ? "Editar campo customizado" : "Novo campo customizado"}
          </span>
          <span className="mt-0.5 text-xs text-gray-500">
            Personalize as informações que aparecem em leads, cards e empresas.
          </span>
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Coluna principal */}
          <div className="lg:col-span-2 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Label */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do campo *
                </label>
                <TextInput
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="Ex: Número do contrato"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <Select value={type} onChange={(e) => setType(e.target.value)}>
                  {Object.entries(TYPE_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>
                      {label}
                    </option>
                  ))}
                </Select>
              </div>
            </div>

            {/* descrição */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição (ajuda)
              </label>
              <textarea
                className="w-full rounded-lg border-gray-300 text-sm focus:border-gray-400 focus:ring-gray-300"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Texto de ajuda que aparece abaixo do campo."
              />
            </div>

            {/* opções para select */}
            {isChoiceType && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <div>
                    <div className="text-sm font-medium text-gray-800">
                      Opções de escolha
                    </div>
                    <div className="text-xs text-gray-500">
                      Digite uma ou várias opções e separe por vírgula ou enter.
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-2 mt-2">
                  <TextInput
                    value={optionsInput}
                    onChange={(e) => setOptionsInput(e.target.value)}
                    placeholder="Ex: Novo, Em andamento, Concluído"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddOptionsFromInput();
                      }
                    }}
                    className="w-full"
                  />
                  <Button
                    type="button"
                    onClick={handleAddOptionsFromInput}
                    className="shrink-0"
                  >
                    Adicionar
                  </Button>
                </div>

                {options.length > 0 && (
                  <div className="mt-3 border-t border-gray-200 pt-2">
                    <ul className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {options.map((opt, index) => (
                        <li
                          key={opt.id || index}
                          className="flex items-center justify-between rounded-lg bg-white border border-gray-200 px-2.5 py-1.5 text-xs"
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-[10px] text-gray-500">
                              {index + 1}
                            </span>
                            <span className="truncate text-gray-800">
                              {opt.label}
                            </span>
                          </div>
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500 text-[11px]"
                            onClick={() => handleRemoveOption(index)}
                          >
                            Remover
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Coluna lateral */}
          <div className="space-y-4">
            {/* Regras de exibição */}
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
              <div className="text-sm font-semibold text-gray-800 mb-2">
                Exibição
              </div>
              <div className="space-y-2 text-xs text-gray-700">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                  />
                  <span>Campo ativo</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                    checked={required}
                    onChange={(e) => setRequired(e.target.checked)}
                  />
                  <span>Obrigatório</span>
                </label>
              </div>
            </div>

            {/* Ordem e preview rápido */}
            <div className="rounded-xl border border-gray-200 bg-white p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-gray-800">
                  Preview
                </div>
                <Badge color="gray" size="xs">
                  {TYPE_LABELS[type]}
                </Badge>
              </div>

              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2">
                <div className="text-[11px] text-gray-500 mb-1">
                  Como o campo deve aparecer:
                </div>
                <div className="text-[11px] font-medium text-gray-700">
                  {label || "Nome do campo"}
                  {required && <span className="text-red-500 ml-0.5">*</span>}
                </div>
                <div className="mt-1">
                  {type === "boolean" && (
                    <div className="flex items-center gap-2 text-xs text-gray-600">
                      <input type="checkbox" disabled />
                      <span>Sim / Não</span>
                    </div>
                  )}
                  {(type === "text" ||
                    type === "number" ||
                    type === "currency" ||
                    type === "date" ||
                    type === "datetime") && (
                    <input
                      disabled
                      className="w-full p-2 rounded border-gray-300 bg-white text-xs text-gray-500"
                      placeholder="Valor será preenchido pelo usuário"
                    />
                  )}
                  {(type === "single_select" || type === "multi_select") && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {(options.length
                        ? options
                        : [{ label: "Opção A" }, { label: "Opção B" }]
                      ).map((opt, index) => (
                        <span
                          key={opt.id || index}
                          className="inline-flex items-center rounded-full bg-white border border-gray-200 px-2 py-0.5 text-[11px] text-gray-700"
                        >
                          {opt.label}
                        </span>
                      ))}
                    </div>
                  )}

                  {type === "textarea" && (
                    <textarea
                      disabled
                      rows={2}
                      className="w-full mt-1 rounded border-gray-300 bg-white text-xs text-gray-500"
                      placeholder="Texto longo"
                    />
                  )}
                  <span className="text-2xs text-gray-500 block italic mt-0.5">
                    {description}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className="flex justify-between">
        <Button color="gray" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!label.trim() || !key.trim() || saving}
        >
          {saving
            ? "Salvando..."
            : isEdit
            ? "Salvar alterações"
            : "Criar campo"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

/* ===================== Página principal ===================== */
export default function CustomFieldsPage() {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [onlyActive, setOnlyActive] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [deletingField, setDeletingField] = useState(null);
  const [selectedFunnelId, setSelectedFunnelId] = useState("");
  const [mobileMenuField, setMobileMenuField] = useState(null);

  // se quiser filtrar por funil via querystring (?f=)
  const location = useLocation();
  const navigate = useNavigate();
  const [availableFunnels, setAvailableFunnels] = useState([]);
  const breadcrumbItems = useMemo(() => {
    const currentFunnel = availableFunnels.find(
      (funnel) => funnel.id === selectedFunnelId
    );
    return [
      {
        label: currentFunnel?.name || "Funis",
        href: selectedFunnelId ? `/crm?f=${selectedFunnelId}` : undefined,
      },
      { label: "Campos customizados", current: true },
    ];
  }, [availableFunnels, selectedFunnelId]);

  const handleChangeFunnel = (newId) => {
    setSelectedFunnelId(newId);

    const params = new URLSearchParams(location.search);
    if (newId) params.set("f", newId);
    else params.delete("f");

    navigate({ search: params.toString() }, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const f = params.get("f");
    if (!f) return;
  }, [location.search]);

  const fetchFields = async () => {
    if (!selectedFunnelId) {
      setFields([]);
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(CUSTOM_FIELDS.LIST(selectedFunnelId));
      const data = res?.data?.data || res?.data || [];
      const list = Array.isArray(data) ? data : [];
      setFields(
        list.sort(
          (a, b) =>
            (a.order ?? 9999) - (b.order ?? 9999) ||
            String(a.label || "").localeCompare(String(b.label || ""))
        )
      );
    } catch (e) {
      console.error("Erro ao listar campos customizados", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedFunnelId) return;
    fetchFields();
  }, [selectedFunnelId]);

  const fetchFunnels = async () => {
    try {
      const res = await axios.get(CRM.FUNNELS);
      const all = res?.data?.data || res?.data || [];
      setAvailableFunnels(all);

      setSelectedFunnelId((prev) => {
        // já tiver algo válido, mantém
        if (prev && all.some((f) => String(f.id) === String(prev))) return prev;

        // tenta pegar da URL ?f=...
        const params = new URLSearchParams(location.search);
        const fromUrl = params.get("f");
        if (fromUrl && all.some((f) => String(f.id) === String(fromUrl))) {
          return fromUrl;
        }

        // senão, usa o primeiro funil
        if (all.length) return String(all[0].id);
        return "";
      });
    } catch (e) {
      console.error("Erro ao buscar funis", e);
    }
  };

  useEffect(() => {
    fetchFunnels();
  }, []);

  useEffect(() => {
    fetchFields();
    fetchFunnels();
  }, []);

  const filtered = useMemo(() => {
    return fields.filter((f) => {
      if (onlyActive && f.active === false) return false;
      if (typeFilter !== "all" && f.type !== typeFilter) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const txt = `${f.label || ""} ${f.key || ""} ${
          f.description || ""
        }`.toLowerCase();
        if (!txt.includes(q)) return false;
      }
      return true;
    });
  }, [fields, onlyActive, typeFilter, search]);

  const openCreate = () => {
    setEditingField(null);
    setModalOpen(true);
  };

  const openEdit = (field) => {
    setEditingField(field);
    setModalOpen(true);
  };

  const confirmDelete = (field) => {
    setDeletingField(field);
  };

  const handleOpenCardMenu = (field) => {
    setMobileMenuField(field);
  };

  const closeMobileMenu = () => {
    setMobileMenuField(null);
  };

  const handleDelete = async () => {
    if (!deletingField?.id) return;
    try {
      await axios.delete(
        CUSTOM_FIELDS.DELETE(selectedFunnelId, deletingField.id)
      );
      await fetchFields();
    } catch (e) {
      console.error("Erro ao excluir campo customizado", e);
    } finally {
      setDeletingField(null);
    }
  };

  const toggleActive = async (field) => {
    try {
      const payload = { active: field.active === false ? true : false };
      await axios.put(CUSTOM_FIELDS.UPDATE(field.id), payload);
      setFields((prev) =>
        prev.map((f) =>
          f.id === field.id ? { ...f, active: payload.active } : f
        )
      );
    } catch (e) {
      console.error("Erro ao alternar ativo", e);
    }
  };

  const toggleRequired = async (field) => {
    try {
      const payload = { required: !field.required };
      await axios.put(CUSTOM_FIELDS.UPDATE(field.id), payload);
      setFields((prev) =>
        prev.map((f) =>
          f.id === field.id ? { ...f, required: payload.required } : f
        )
      );
    } catch (e) {
      console.error("Erro ao alternar obrigatório", e);
    }
  };

  return (
    <>
      <CorporateBreadcrumb items={breadcrumbItems} />
      {/* Header */}
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between mt-4">
        <div className="space-y-1">
          <p className="text-sm uppercase tracking-[0.4em] text-orange-500">
            Funil
          </p>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">
                Campos customizados
              </h1>
            </div>
          </div>
        </div>

        <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
          {/* Select de funil */}
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-gray-500">Funil</span>
            <Select
              value={selectedFunnelId}
              onChange={(e) => handleChangeFunnel(e.target.value)}
              className="min-w-[200px] flex-1"
            >
              {availableFunnels.length === 0 && (
                <option value="">Nenhum funil encontrado</option>
              )}
              {availableFunnels.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name || f.title || `Funil #${f.id}`}
                </option>
              ))}
            </Select>
          </div>

          {/* Botão novo campo */}
          <Button
            className="mt-1 sm:mt-0 hidden md:inline-flex"
            onClick={openCreate}
            disabled={!selectedFunnelId}
          >
            <div className="flex items-center">
              <HiPlus className="h-4 w-4 mr-1" />
              <span> Novo campo customizado</span>
            </div>
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div className="flex flex-1 flex-wrap gap-2">
          <div className="relative max-w-xs w-full">
            <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-400">
              <HiSearch className="h-4 w-4" />
            </span>
            <TextInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 flex-1"
              placeholder="Buscar por nome, chave ou descrição…"
            />
          </div>

          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="max-w-[170px]"
          >
            <option value="all">Todos os tipos</option>
            {Object.entries(TYPE_LABELS).map(([val, label]) => (
              <option key={val} value={val}>
                {label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Lista */}
      <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/60 px-3 py-2 text-[11px] text-gray-500 flex justify-between items-center">
          <span>
            {filtered.length} campo{filtered.length === 1 ? "" : "s"} encontrado
            {onlyActive && " (apenas ativos)"}
          </span>
          <span>Campos são aplicados automaticamente após salvar.</span>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10 gap-3">
            <Spinner />
            <span className="text-sm text-gray-600">
              Carregando campos customizados…
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center text-sm text-gray-600">
            Nenhum campo encontrado.{" "}
            <button
              className="text-primary underline-offset-2 underline"
              onClick={openCreate}
            >
              Criar primeiro campo
            </button>
            .
          </div>
        ) : (
          <>
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500">
                    <th className="px-3 py-2">Campo</th>
                    <th className="px-3 py-2 text-center">Tipo</th>
                    <th className="px-3 py-2 text-center">Obrigatório</th>
                    <th className="px-3 py-2 text-center">Ativo</th>
                    <th className="px-3 py-2 text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((f, i) => (
                    <tr
                      key={f.id || i}
                      className={cx(
                        "border-b border-gray-50 hover:bg-gray-50/80 transition-colors",
                        !f.active && "opacity-75"
                      )}
                    >
                      <td className="px-3 py-2 align-top">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900">
                              {f.label}
                            </span>
                            {f.group && (
                              <span className="text-[10px] rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">
                                {f.group}
                              </span>
                            )}
                          </div>
                          {f.description && (
                            <div className="mt-0.5 text-[11px] text-gray-500 line-clamp-2">
                              {f.description}
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-3 py-2 align-top text-center">
                        <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 border border-indigo-100 px-2 py-0.5 text-[11px] text-indigo-800">
                          {TYPE_LABELS[f.type] || f.type}
                        </span>
                      </td>

                      <td className="px-3 py-2 text-center align-top">
                        <button
                          type="button"
                          onClick={() => toggleRequired(f)}
                          className={cx(
                            "inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs",
                            f.required
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 bg-white text-gray-400 hover:text-gray-600"
                          )}
                          title="Alternar obrigatório"
                        >
                          {f.required ? (
                            <HiOutlineCheckCircle className="h-4 w-4" />
                          ) : (
                            <HiOutlineXCircle className="h-4 w-4" />
                          )}
                        </button>
                      </td>

                      <td className="px-3 py-2 text-center align-top">
                        <button
                          type="button"
                          onClick={() => toggleActive(f)}
                          className={cx(
                            "inline-flex items-center justify-center h-7 w-7 rounded-full border text-xs",
                            f.active !== false
                              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                              : "border-gray-200 bg-white text-gray-400 hover:text-gray-600"
                          )}
                          title="Ativar / desativar"
                        >
                          {f.active !== false ? (
                            <HiOutlineCheckCircle className="h-4 w-4" />
                          ) : (
                            <HiOutlineXCircle className="h-4 w-4" />
                          )}
                        </button>
                      </td>

                      <td className="px-3 py-2 text-center align-top">
                        <div className="inline-flex items-center gap-1.5">
                          <Button
                            size="xs"
                            color="light"
                            onClick={() => openEdit(f)}
                          >
                            <HiOutlinePencil className="h-4 w-4 mr-1" />
                          </Button>
                          <Button
                            size="xs"
                            color="failure"
                            onClick={() => confirmDelete(f)}
                          >
                            <HiOutlineTrash className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3 px-3 py-4">
              {filtered.map((f, i) => (
                <button
                  key={f.id || i}
                  type="button"
                  onClick={() => handleOpenCardMenu(f)}
                  className={cx(
                    "w-full rounded-2xl border border-gray-200 bg-white p-4 text-left shadow-sm transition hover:border-slate-300",
                    f.active === false && "opacity-70"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-base font-semibold text-gray-900">
                        {f.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {TYPE_LABELS[f.type] || f.type}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 text-right">
                      {f.required ? "Obrigatório" : "Opcional"}
                    </div>
                  </div>
                  {f.description && (
                    <p className="mt-2 text-xs text-gray-500 line-clamp-3">
                      {f.description}
                    </p>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Modal de criação/edição */}
      <FieldFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSaved={fetchFields}
        initialField={editingField}
        availableFunnels={availableFunnels}
        funnelId={selectedFunnelId}
      />

      {/* Modal de confirmação de exclusão */}
      <Modal
        show={!!deletingField}
        onClose={() => setDeletingField(null)}
        size="md"
      >
        <Modal.Header>
          <div className="flex items-center gap-2 text-red-700">
            <HiOutlineExclamation className="h-5 w-5" />
            <span>Excluir campo customizado</span>
          </div>
        </Modal.Header>
        <Modal.Body>
          <p className="text-sm text-gray-700">
            Tem certeza que deseja excluir o campo{" "}
            <span className="font-semibold">
              {deletingField?.label || deletingField?.key}
            </span>
            ? Essa ação pode impactar formulários e relatórios que usam esse
            campo.
          </p>
        </Modal.Body>
        <Modal.Footer className="flex justify-between">
          <Button color="gray" onClick={() => setDeletingField(null)}>
            Cancelar
          </Button>
          <Button color="failure" onClick={handleDelete}>
            Excluir
          </Button>
        </Modal.Footer>
      </Modal>

      {mobileMenuField && (
        <div className="fixed inset-0 z-50 flex flex-col justify-end md:hidden">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={closeMobileMenu}
          />
          <div className="relative rounded-t-3xl bg-white p-5 shadow-2xl">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {mobileMenuField.label}
                </p>
                <p className="text-xs text-gray-500">
                  {TYPE_LABELS[mobileMenuField.type] || mobileMenuField.type}
                </p>
              </div>
              <button
                type="button"
                onClick={closeMobileMenu}
                className="rounded-full border border-gray-200 p-1.5 text-gray-500"
              >
                <HiOutlineXCircle className="h-4 w-4" />
              </button>
            </div>
            {mobileMenuField.description && (
              <p className="mt-3 text-xs text-gray-500">
                {mobileMenuField.description}
              </p>
            )}
            <div className="mt-4 space-y-2">
              <Button
                className="w-full justify-center"
                onClick={() => {
                  openEdit(mobileMenuField);
                  closeMobileMenu();
                }}
                disabled={!mobileMenuField}
              >
                Editar campo
              </Button>
              <Button
                className="w-full justify-center"
                color="light"
                onClick={() => {
                  toggleRequired(mobileMenuField);
                  closeMobileMenu();
                }}
              >
                {mobileMenuField.required
                  ? "Marcar como opcional"
                  : "Marcar como obrigatório"}
              </Button>
              <Button
                className="w-full justify-center"
                color={mobileMenuField.active !== false ? "failure" : "gray"}
                onClick={() => {
                  toggleActive(mobileMenuField);
                  closeMobileMenu();
                }}
              >
                {mobileMenuField.active !== false
                  ? "Desativar campo"
                  : "Reativar campo"}
              </Button>
              <Button
                className="w-full justify-center"
                color="failure"
                onClick={() => {
                  confirmDelete(mobileMenuField);
                  closeMobileMenu();
                }}
              >
                Excluir campo
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="md:hidden">
        <div className="fixed bottom-4 right-4 z-40">
          <button
            type="button"
            onClick={openCreate}
            disabled={!selectedFunnelId}
            className="flex h-12 w-12 items-center justify-center rounded-full bg-orange-500 text-white shadow-xl transition hover:bg-orange-600 disabled:bg-orange-200"
          >
            <HiPlus className="h-5 w-5" />
          </button>
        </div>
      </div>
    </>
  );
}
